/**
 * Edit Case Modal Component
 * Modal for editing case details
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import { CASE_TYPES } from '../../constants/caseTypes';

const UPDATE_CASE = gql`
  mutation UpdateCase($id: ID!, $input: UpdateCaseInput!) {
    updateCase(id: $id, input: $input) {
      id
      caseNumber
      caseType
      status
      description
      incidentDate
      assignedAttorneyId
      assignedCaseManagerId
    }
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      role
    }
  }
`;

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface EditCaseModalProps {
  caseData: {
    id: string;
    caseNumber: string;
    caseType: string;
    status: string;
    description?: string;
    incidentDate?: string;
    assignedAttorneyId?: string;
    assignedCaseManagerId?: string;
  };
  onClose: () => void;
  onSuccess?: () => void;
}

export function EditCaseModal({ caseData, onClose, onSuccess }: EditCaseModalProps) {
  const [caseType, setCaseType] = useState(caseData.caseType || '');
  const [customCaseType, setCustomCaseType] = useState('');
  const [status, setStatus] = useState(caseData.status || 'NEW');
  const [description, setDescription] = useState(caseData.description || '');
  const [incidentDate, setIncidentDate] = useState(caseData.incidentDate || '');
  const [assignedAttorneyId, setAssignedAttorneyId] = useState(caseData.assignedAttorneyId || '');
  const [assignedCaseManagerId, setAssignedCaseManagerId] = useState(caseData.assignedCaseManagerId || '');

  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);
  const [updateCase, { loading }] = useMutation(UPDATE_CASE);

  const staffMembers = staffData?.listStaff || [];
  const attorneys = staffMembers.filter((s: any) => s.role === 'ATTORNEY');
  const caseManagers = staffMembers.filter((s: any) => s.role === 'CASE_MANAGER' || s.role === 'LEAD_CASE_MANAGER' || s.role === 'ADMIN');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalCaseType = caseType === 'Other' ? customCaseType : caseType;
    if (!finalCaseType) {
      alert('Please select or enter a case type');
      return;
    }

    try {
      await updateCase({
        variables: {
          id: caseData.id,
          input: {
            caseType: finalCaseType,
            status,
            description: description || undefined,
            incidentDate: incidentDate || undefined,
            assignedAttorneyId: assignedAttorneyId || undefined,
            assignedCaseManagerId: assignedCaseManagerId || undefined,
          },
        },
      });

      alert('Case updated successfully!');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error updating case:', error);
      alert(`Error updating case: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Edit Case #{caseData.caseNumber}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Case Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Case Type <span className="text-red-500">*</span>
            </label>
            <select
              value={caseType}
              onChange={(e) => setCaseType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              required
            >
              <option value="">Select Case Type</option>
              {CASE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
              <option value="Other">Other (Custom)</option>
            </select>
          </div>

          {/* Custom Case Type */}
          {caseType === 'Other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Case Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customCaseType}
                onChange={(e) => setCustomCaseType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                placeholder="Enter custom case type"
                required
              />
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              required
            >
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="NEGOTIATION">Negotiation</option>
              <option value="LITIGATION">Litigation</option>
              <option value="SETTLED">Settled</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              placeholder="Case description..."
            />
          </div>

          {/* Incident Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Incident Date
            </label>
            <input
              type="date"
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            />
          </div>

          {/* Assigned Attorney */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Attorney
            </label>
            <select
              value={assignedAttorneyId}
              onChange={(e) => setAssignedAttorneyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {attorneys.map((attorney: any) => (
                <option key={attorney.id} value={attorney.id}>
                  {attorney.firstName} {attorney.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned Case Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Case Manager
            </label>
            <select
              value={assignedCaseManagerId}
              onChange={(e) => setAssignedCaseManagerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {caseManagers.map((manager: any) => (
                <option key={manager.id} value={manager.id}>
                  {manager.firstName} {manager.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Updating...' : 'Update Case'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
